import React, { useRef, useEffect, useState } from 'react';

import {
  FormControl,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useField } from '@unform/core';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { canURL } from '~/helpers';
import api from '~/services/api';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    height: 230,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}));

function CheckboxGroup({
  name,
  label,
  getValue,
  url,
  title,
  unique,
  disabled,
}) {
  const inputRef = useRef(null);
  const classes = useStyles();
  const permission = isEqual(canURL(), { read: true });

  const { fieldName, defaultValue, registerField } = useField(name);

  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState(defaultValue || []);

  function handleToggle(value) {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    inputRef.current.value = newChecked;
    getValue(newChecked);
  }

  async function handleGetData() {
    const { data } = await api.get(url, {
      params: { pagesize: 99999, pesquisalista: true },
    });

    setItems(data.items);
  }

  async function handleSelectAll() {
    const newItems = items.map((value) => value[unique]);

    if (items.length === checked.length) {
      setChecked([]);
      inputRef.current.value = [];
      getValue([]);
    } else {
      setChecked(newItems);
      inputRef.current.value = newItems;
      getValue(newItems);
    }
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    handleGetData();
  }, []);

  return (
    <FormControl style={{ width: '100%' }}>
      <input ref={inputRef} type="hidden" />

      <Card>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Checkbox
              onClick={() => handleSelectAll()}
              checked={checked.length === items.length && checked.length !== 0}
              indeterminate={
                checked.length !== items.length && checked.length !== 0
              }
              disabled={permission || items.length === 0}
              inputProps={{ 'aria-label': 'itens selecionados' }}
            />
          }
          title={title}
          subheader={`${checked.length}/${items.length} selecionados`}
        />

        <Divider />

        <List className={classes.list} dense component="div" role="list">
          {items.map((value) => {
            const labelId = `transfer-list-all-item-${value[unique]}-label`;

            return (
              <ListItem
                key={value[unique]}
                role="listitem"
                button
                onClick={() =>
                  permission ? undefined : handleToggle(value[unique])
                }
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.indexOf(value[unique]) !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': labelId }}
                    disabled={permission || disabled}
                  />
                </ListItemIcon>
                <ListItemText primary={value[label]} />
              </ListItem>
            );
          })}
          <ListItem />
        </List>
      </Card>
    </FormControl>
  );
}

CheckboxGroup.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  unique: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  url: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

CheckboxGroup.defaultProps = {
  getValue: () => {},
  disabled: false,
};

export default CheckboxGroup;
